import React from 'react';
import { X, Bell, MessageSquare, Map, MapPin, Star, CheckCheck, Snowflake, AlertTriangle, CreditCard, Clock, Bug, Route, Navigation, Lightbulb, Heart, Tent } from 'lucide-react';
import { AppNotification, AppUser } from '../types';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../services/localeHelpers';

interface NotificationsPanelProps {
  notifications: AppNotification[];
  currentUser: AppUser;
  onClose: () => void;
  onMarkAsRead: (notif: AppNotification) => void;
  onMarkAllRead: () => void;
  onOpenTesterInbox?: () => void;
  onOpenAdminTesterThread?: (testerUid: string) => void;
  onOpenAdminSuggestions?: () => void;
  onOpenCollabMap?: (mapId: string) => void;
  onGoToSpot?: (spotId: string, opts?: { verify?: boolean }) => void;
  onOpenRoute?: (routeId: string) => void;
  onOpenUpgrade?: () => void;
}

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'nå nettopp';
  if (mins < 60) return `${mins}m siden`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}t siden`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d siden`;
  return new Date(ms).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short' });
}

const TYPE_CONFIG: Record<AppNotification['type'], { icon: React.ReactNode; color: string; label: string }> = {
  tester_message: {
    icon: <MessageSquare size={16} />,
    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    label: 'Melding fra Fricamp',
  },
  collab_invite: {
    icon: <Map size={16} />,
    color: 'text-sky-400 bg-sky-500/10 border-sky-500/20',
    label: 'Kartinvitasjon',
  },
  collab_message: {
    icon: <MessageSquare size={16} />,
    color: 'text-sky-400 bg-sky-500/10 border-sky-500/20',
    label: 'Kartmelding',
  },
  new_spot: {
    icon: <MapPin size={16} />,
    color: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    label: 'Ny plass',
  },
  new_review: {
    icon: <Star size={16} />,
    color: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
    label: 'Ny anmeldelse',
  },
  county_new_spot: {
    icon: <MapPin size={16} />,
    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    label: 'Ny plass i fylke',
  },
  county_new_review: {
    icon: <Star size={16} />,
    color: 'text-teal-400 bg-teal-500/10 border-teal-500/20',
    label: 'Ny anmeldelse i fylke',
  },
  system_winter: {
    icon: <Snowflake size={16} />,
    color: 'text-sky-300 bg-sky-500/10 border-sky-500/20',
    label: 'Vintersesong',
  },
  varsom: {
    icon: <AlertTriangle size={16} />,
    color: 'text-red-400 bg-red-500/10 border-red-500/20',
    label: 'Farevarsel',
  },
  subscription_reset: {
    icon: <CreditCard size={16} />,
    color: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
    label: 'Abonnement',
  },
  tester_expiry_warning: {
    icon: <Clock size={16} />,
    color: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    label: 'Testperiode',
  },
  tester_expired: {
    icon: <AlertTriangle size={16} />,
    color: 'text-red-400 bg-red-500/10 border-red-500/20',
    label: 'Testperiode utløpt',
  },
  location_sharing: {
    icon: <Navigation size={16} />,
    color: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    label: 'Posisjonsdeling',
  },
  route_approved: {
    icon: <Route size={16} />,
    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    label: 'Rute godkjent',
  },
  route_rejected: {
    icon: <Route size={16} />,
    color: 'text-red-400 bg-red-500/10 border-red-500/20',
    label: 'Rute avvist',
  },
  admin_tester_message: {
    icon: <MessageSquare size={16} />,
    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    label: 'Brukermelding',
  },
  admin_error_report: {
    icon: <Bug size={16} />,
    color: 'text-red-400 bg-red-500/10 border-red-500/20',
    label: 'Feilrapport',
  },
  admin_category_suggestion: {
    icon: <Lightbulb size={16} />,
    color: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    label: 'Forbedringsforslag',
  },
  verify_nudge: {
    icon: <CheckCheck size={16} />,
    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    label: 'Verifiser plass',
  },
  favorite_added: {
    icon: <Heart size={16} />,
    color: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
    label: 'Ny favoritt',
  },
  visit_added: {
    icon: <Tent size={16} />,
    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    label: 'Ny gjest',
  },
  route_recommendation: {
    icon: <Route size={16} />,
    color: 'text-sky-400 bg-sky-500/10 border-sky-500/20',
    label: 'Ruteforslag',
  },
  route_view_milestone: {
    icon: <Route size={16} />,
    color: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    label: 'Rute-milepæl',
  },
  subscription_welcome: {
    icon: <CreditCard size={16} />,
    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    label: 'Velkommen',
  },
  subscription_ended: {
    icon: <CreditCard size={16} />,
    color: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
    label: 'Abonnement avsluttet',
  },
  tester_approved: {
    icon: <CheckCheck size={16} />,
    color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    label: 'Testtilgang',
  },
  tier_progress: {
    icon: <Star size={16} />,
    color: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    label: 'Nivå',
  },
  unread_reminder: {
    icon: <Bell size={16} />,
    color: 'text-[var(--text-muted)] bg-[var(--bg-surface-alt)] border-[var(--border-primary)]',
    label: 'Påminnelse',
  },
};

// Varsler som peker på en plass (data.spotId → zoom til plassen på kartet).
const SPOT_LINK_TYPES: ReadonlyArray<AppNotification['type']> = [
  'new_spot', 'county_new_spot', 'new_review', 'county_new_review', 'favorite_added', 'visit_added',
];
// Varsler som peker på en rute (data.routeId → åpne rutekortet).
const ROUTE_LINK_TYPES: ReadonlyArray<AppNotification['type']> = [
  'route_recommendation', 'route_view_milestone', 'route_approved', 'route_rejected',
];
// Abonnement-varsler → åpne oppgraderings-/abonnementsvisningen (ingen data nødvendig).
const SUBSCRIPTION_TYPES: ReadonlyArray<AppNotification['type']> = [
  'subscription_welcome', 'subscription_ended', 'subscription_reset',
];

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  notifications,
  currentUser,
  onClose,
  onMarkAsRead,
  onMarkAllRead,
  onOpenTesterInbox,
  onOpenAdminTesterThread,
  onOpenAdminSuggestions,
  onOpenCollabMap,
  onGoToSpot,
  onOpenRoute,
  onOpenUpgrade,
}) => {
  const { t } = useTranslation();
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleClick = async (notif: AppNotification) => {
    if (!notif.isRead) {
      onMarkAsRead(notif);
    }
    if (notif.type === 'tester_message') {
      onOpenTesterInbox?.();
      onClose();
    } else if (notif.type === 'admin_tester_message' && notif.data?.testerUid) {
      onOpenAdminTesterThread?.(notif.data.testerUid);
      onClose();
    } else if (notif.type === 'admin_category_suggestion') {
      // Åpne forslags-fanen (viser selve endringen). Bevisst IKKE en chat med
      // brukeren — anonyme forslag skal forbli anonyme. Trenger ingen data.
      onOpenAdminSuggestions?.();
      onClose();
    } else if (notif.type === 'collab_invite' && notif.data?.mapId) {
      onOpenCollabMap?.(notif.data.mapId);
      onClose();
    } else if (notif.type === 'collab_message' && notif.data?.mapId) {
      // Sammenslått chat-varsel («N nye meldinger») → åpne kartet (chatten).
      onOpenCollabMap?.(notif.data.mapId);
      onClose();
    } else if (notif.type === 'verify_nudge' && notif.data?.spotId) {
      // Verifiser-nudge: zoom til plassen OG åpne verifiseringen i ett tapp.
      onGoToSpot?.(notif.data.spotId, { verify: true });
      onClose();
    } else if (SPOT_LINK_TYPES.includes(notif.type) && notif.data?.spotId) {
      // favorite_added («noen liker plassen din») og visit_added («første gjest»)
      // sendes fra functions med data.spotId → zoom til plassen som ble likt/besøkt.
      onGoToSpot?.(notif.data.spotId);
      onClose();
    } else if (ROUTE_LINK_TYPES.includes(notif.type) && notif.data?.routeId) {
      // Rute-varsler (forslag/milepæl/godkjent/avvist) bærer data.routeId → åpne rutekortet.
      onOpenRoute?.(notif.data.routeId);
      onClose();
    } else if (SUBSCRIPTION_TYPES.includes(notif.type)) {
      // Abonnement-varsler har ingen navigasjons-id → åpne abonnements-/oppgraderingsvisningen.
      onOpenUpgrade?.();
      onClose();
    } else if (notif.type === 'location_sharing' && notif.data?.mapId) {
      // Noen deler posisjon på et samarbeidskart → åpne det kartet.
      onOpenCollabMap?.(notif.data.mapId);
      onClose();
    }
  };

  return (
    <div className="fixed left-0 right-0 bottom-0 z-[9000] flex" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative ml-auto w-full max-w-sm h-full bg-[var(--bg-base)] border-l border-[var(--border-primary)] shadow-[0_0_60px_rgba(0,0,0,0.8)] flex flex-col animate-in slide-in-from-right-4 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-primary)]">
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-[var(--text-secondary)]" />
            <h2 className="text-[12px] font-black text-[var(--text-primary)] uppercase tracking-widest">{t("auto.notificationspanel.varsler", "Varsler")}</h2>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-3xs font-black px-1.5 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllRead}
                className="flex items-center gap-1.5 text-2xs font-black text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors uppercase tracking-widest"
                title={t("auto.notificationspanel.title.merk_alle_som_lest", "Merk alle som lest")}
              >
                <CheckCheck size={13} />
                {t("auto.notificationspanel.merk_alle", "Merk alle")}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors rounded-lg hover:bg-[var(--bg-surface-alt)]"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
              <Bell size={32} className="text-[var(--text-muted)]" />
              <p className="text-tiny text-[var(--text-muted)] font-medium">{t("auto.notificationspanel.ingen_varsler_enda", "Ingen varsler enda")}</p>
              <p className="text-2xs text-[var(--text-muted)]">{t("auto.notificationspanel.du_vil_se_varsler_her_nar_noe_", "Du vil se varsler her når noe nytt skjer")}</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--border-primary)]">
              {notifications.map(notif => {
                const cfg = TYPE_CONFIG[notif.type] ?? TYPE_CONFIG.new_spot;
                const hasSpotLink = SPOT_LINK_TYPES.includes(notif.type) && !!notif.data?.spotId;
                const hasVerifyLink = notif.type === 'verify_nudge' && !!notif.data?.spotId;
                const hasAdminThreadLink = notif.type === 'admin_tester_message' && !!notif.data?.testerUid;
                const hasRouteLink = ROUTE_LINK_TYPES.includes(notif.type) && !!notif.data?.routeId;
                const hasLocationShare = notif.type === 'location_sharing' && !!notif.data?.mapId;
                // Forslag og abonnement-varsler er alltid klikkbare (åpner fane/visning, uavhengig av data).
                const isClickable = notif.type === 'tester_message' || notif.type === 'collab_invite'
                  || (notif.type === 'collab_message' && !!notif.data?.mapId)
                  || notif.type === 'admin_category_suggestion' || SUBSCRIPTION_TYPES.includes(notif.type)
                  || hasSpotLink || hasVerifyLink || hasAdminThreadLink || hasRouteLink || hasLocationShare;
                return (
                  <div
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`flex items-start gap-3 px-5 py-4 transition-colors ${isClickable ? 'cursor-pointer hover:bg-white/3' : ''} ${!notif.isRead ? 'bg-[var(--bg-surface-alt)]' : ''}`}
                  >
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-xl border flex items-center justify-center mt-0.5 ${cfg.color}`}>
                      {cfg.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-[12px] font-bold leading-tight mb-0.5 ${notif.isRead ? 'text-[var(--text-secondary)]' : 'text-[var(--text-primary)]'}`}>
                        {notif.title}
                      </p>
                      <p className="text-tiny text-[var(--text-muted)] leading-snug line-clamp-2">{notif.body}</p>
                      <p className="text-2xs text-[var(--text-muted)] mt-1.5">{relativeTime(notif.createdAt)}</p>
                    </div>

                    {/* Unread dot */}
                    {!notif.isRead && (
                      <div className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-400 mt-2" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPanel;
